// client/src/components/billingTransactions/BylawTransactionList.js
import React, { useState, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams, useLocation, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Container,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Box,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Alert,
  Snackbar,
  CircularProgress,
  Chip,
  Breadcrumbs,
  Link,
  Tooltip
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  ArrowBack as ArrowBackIcon,
  Lock as LockIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { AuthContext } from '../../contexts/AuthContext';
import { LocaleContext } from '../../contexts/LocaleContext';
import billingTransactionService from '../../services/billingTransactionService';
import paymentBylawService from '../../services/paymentBylawService';

const BylawTransactionList = () => {
  const { t, i18n } = useTranslation();
  const { isAdmin } = useContext(AuthContext);
  const { direction } = useContext(LocaleContext);
  const { id: bylawId } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const readOnly = location.state?.readOnly || false;
  
  const [transactions, setTransactions] = useState([]);
  const [bylaw, setBylaw] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [transactionToDelete, setTransactionToDelete] = useState(null);

  // Current language
  const lang = i18n.language;

  // Function to load transactions
  const loadTransactions = async () => {
    try {
      setLoading(true);
      
      // Get bylaw data
      const bylawResponse = await paymentBylawService.getPaymentBylaw(bylawId);
      setBylaw(bylawResponse.data);
      
      // Get transactions for this bylaw
      const response = await billingTransactionService.getBylawTransactions(bylawId);
      setTransactions(response.data);

      setError('');
    } catch (err) {
      setError(t('errorLoadingTransactions'));
    } finally {
      setLoading(false);
    }
  };

  // Load transactions on component mount
  useEffect(() => {
    loadTransactions();
  }, [bylawId]);

  // Handle transaction deletion
  const handleDeleteClick = (transaction) => {
    setTransactionToDelete(transaction);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    try {
      await billingTransactionService.deleteBillingTransaction(transactionToDelete.id);
      setSuccess(t('transactionDeletedSuccess'));
      loadTransactions();
    } catch (err) {
      setError(t('errorDeletingTransaction'));
    } finally {
      setDeleteDialogOpen(false);
      setTransactionToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setTransactionToDelete(null);
  };

  const handleCloseAlert = () => {
    setSuccess('');
    setError('');
  };

  // Format currency
  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', { 
      style: 'currency', 
      currency: 'USD' 
    }).format(value);
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4 }}>
      {/* Breadcrumbs navigation */}
      <Breadcrumbs separator="›" aria-label="breadcrumb" sx={{ mb: 2 }}>
        <Link component={RouterLink} to="/payment-bylaws" underline="hover" color="inherit">
          {t('paymentBylaws')}
        </Link>
        <Typography color="text.primary">
          {bylaw?.name || t('bylawTransactions')}
        </Typography>
      </Breadcrumbs>
      
      <Typography variant="h4" gutterBottom>
        {t('bylawTransactions')}
        {readOnly && (
          <Chip 
            label={t('readOnlyMode')} 
            color="warning" 
            size="small" 
            icon={<LockIcon />}
            sx={{ ml: 2, verticalAlign: 'middle' }}
          />
        )}
      </Typography>
      
      {bylaw && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Typography variant="h6">{bylaw.name}</Typography>
          <Typography variant="body2">
            {t('type')}: {t(bylaw.bylaw_type)}
            {bylaw.faculty && ` - ${bylaw.faculty[`name_${lang}`]}`}
          </Typography>
          <Typography variant="body2">
            {t('status')}: 
            <Chip 
              label={bylaw.is_approved ? t('approved') : t('pending')}
              color={bylaw.is_approved ? 'success' : 'warning'}
              size="small"
              sx={{ ml: 1 }}
            />
          </Typography>
        </Paper>
      )}

      {/* Alert messages */}
      <Snackbar 
        open={!!success} 
        autoHideDuration={6000} 
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseAlert} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>

      <Snackbar 
        open={!!error} 
        autoHideDuration={6000} 
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseAlert} severity="error" sx={{ width: '100%' }}>
          {error}
        </Alert>
      </Snackbar>

      {/* Add Transaction button - only when not in read-only mode */}
      {!readOnly && (
        <Box sx={{ mb: 2, textAlign: direction === 'ltr' ? 'right' : 'left' }}>
          <Button 
            variant="contained" 
            color="primary" 
            startIcon={<AddIcon />}
            component={RouterLink}
            to={`/payment-bylaws/${bylawId}/transactions/create`}
          >
            {t('addTransaction')}
          </Button>
        </Box>
      )}

      {/* Back button */}
      <Box sx={{ mb: 2 }}>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/payment-bylaws')}
        >
          {t('backToBylaws')}
        </Button>
      </Box>

      {/* Transactions Table */}
      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      ) : transactions.length === 0 ? (
        <Paper sx={{ p: 2, textAlign: 'center' }}>
          <Typography variant="body1">
            {t('noTransactionsFound')}
          </Typography>
        </Paper>
      ) : (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>{t('paymentItem')}</TableCell>
                
                <TableCell align="right">{t('totalValue')}</TableCell>
                <TableCell align="center">{t('distributionCount')}</TableCell>
                <TableCell align="center">{t('actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {transactions.map((transaction) => (
                <TableRow key={transaction.id}>
                  <TableCell>{transaction.paymentItem?.name || ''}</TableCell>
                  
                  <TableCell align="right">{formatCurrency(transaction.total_value)}</TableCell>
                  <TableCell align="center">
                    <Chip 
                      label={transaction.distributions?.length || 0} 
                      color="primary" 
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="center">
                    <IconButton
                      color="info"
                      onClick={() => navigate(`/payment-bylaws/${bylawId}/transactions/${transaction.id}`)}
                    >
                      <ViewIcon />
                    </IconButton>
                    
                    {!readOnly && !transaction.hasreceipts && (
                      <>
                        <IconButton
                          color="primary"
                          component={RouterLink}
                          to={`/payment-bylaws/${bylawId}/transactions/edit/${transaction.id}`}
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          color="error"
                          onClick={() => handleDeleteClick(transaction)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleDeleteCancel}
      >
        <DialogTitle>{t('confirmDelete')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t('deleteTransactionConfirmation', { 
              name: transactionToDelete?.paymentItem?.name || t('thisTransaction')
            })}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>{t('cancel')}</Button>
          <Button onClick={handleDeleteConfirm} color="error" autoFocus>
            {t('delete')}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default BylawTransactionList;